import type { ApiEnvelope } from '@/types/api'
import type { Choice, LogicJump, PublicForm, Question } from '@/types/form'

export const MIAOLIN_CHANNEL_QUESTION_ID = 'q-miaolin-33'
export const MIAOLIN_DYNAMIC_CHANNEL_TYPE_QUESTION_ID = 'q-miaolin-34'
export const MIAOLIN_CHANNEL_TYPE_SPLIT_FROM_ID = 'q-miaolin-34'
export const MIAOLIN_CHANNEL_TYPE_LOGIC_PREFIX = 'logic-miaolin-customer-channel-type-'

export type MiaolinDynamicOption = {
  label: string
  value: string
  disabled?: boolean
}

export type MiaolinChannelTypesResult = {
  enabled: boolean
  providerMode: 'webhook'
  providerState: 'configured'
  source: 'miaolin_channel_types_webhook'
  channel: string
  options: MiaolinDynamicOption[]
  fallbackUsed: boolean
  safeMessage?: string
}

export async function fetchMiaolinChannelTypeOptions({
  formId,
  channel,
  signal,
}: {
  formId: string
  channel: string
  signal?: AbortSignal
}): Promise<MiaolinChannelTypesResult | null> {
  const trimmedChannel = channel.trim()
  if (!trimmedChannel) return null

  try {
    const response = await fetch(
      `/api/public/forms/${encodeURIComponent(formId)}/miaolin-channel-types`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ channel: trimmedChannel }),
        signal,
      },
    )
    if (!response.ok) return null

    const envelope = (await response.json()) as ApiEnvelope<MiaolinChannelTypesResult>
    if (!envelope.data?.enabled || envelope.data.options.length === 0) return null
    return envelope.data
  } catch {
    return null
  }
}

export function getMiaolinCustomerTypeQuestionIds(questions: Question[]) {
  return questions
    .filter((question) => isMiaolinCustomerTypeQuestion(question))
    .map((question) => question.id)
}

export function isMiaolinChannelWebhookQuestion(question: Question) {
  return (
    isMiaolinChannelQuestion(question) &&
    isRecord(question.settings.dynamicOptions) &&
    question.settings.dynamicOptions.kind === 'miaolin_channels'
  )
}

export function isMiaolinDynamicChannelTypeQuestion(question: Question) {
  return (
    isMiaolinSingleCustomerTypeQuestion(question) &&
    isRecord(question.settings.dynamicOptions) &&
    question.settings.dynamicOptions.kind === 'miaolin_channel_types'
  )
}

export function getMiaolinChannelQuestionId(questions: Question[]) {
  return questions.find((question) => isMiaolinChannelQuestion(question))?.id ?? null
}

export function getMiaolinDynamicChannelTypeQuestionId(questions: Question[]) {
  return questions.find((question) => isMiaolinSingleCustomerTypeQuestion(question))?.id ?? null
}

export function shouldHideEmptyMiaolinDynamicChannelTypeQuestion(question: Question) {
  return isMiaolinDynamicChannelTypeQuestion(question) && question.choices.length === 0
}

export function buildMiaolinChannelTypeFallbackForm(baseForm: PublicForm): PublicForm {
  const channelQuestionId =
    getMiaolinChannelQuestionId(baseForm.questions) ?? MIAOLIN_CHANNEL_QUESTION_ID
  const dynamicTypeQuestionId =
    getMiaolinDynamicChannelTypeQuestionId(baseForm.questions) ??
    MIAOLIN_DYNAMIC_CHANNEL_TYPE_QUESTION_ID
  const legacyTypeQuestionIds = getMiaolinLegacyCustomerTypeQuestionIds(baseForm.questions)
  const questions = baseForm.questions
    .filter((question) => !isMiaolinLegacyCustomerTypeQuestion(question))
    .map((question) => {
      if (!isMiaolinDynamicChannelTypeQuestion(question)) return question

      return {
        ...question,
        settings: {
          ...question.settings,
          placeholder: '請先選擇客戶通路別',
          conditionalDisplay: {
            controllerQuestionId: channelQuestionId,
            mode: 'show_when_answered',
            source: 'miaolin-channel-types-fallback',
            sourceControllerQuestionId: MIAOLIN_CHANNEL_QUESTION_ID,
          },
          dynamicOptions: {
            kind: 'miaolin_channel_types',
            providerMode: 'webhook',
            sourceLabel: 'Miaolin channel type webhook',
            controllerQuestionId: channelQuestionId,
            sourceControllerQuestionId: MIAOLIN_CHANNEL_QUESTION_ID,
            fallback: 'dependent_question_choices',
          },
        },
      } satisfies Question
    })

  return cloneFormWithRuntimeShape(baseForm, {
    questions,
    logicJumps: baseForm.logicJumps.filter(
      (jump) =>
        !isMiaolinChannelTypeLogicJump(jump, {
          channelQuestionId,
          dynamicTypeQuestionId,
          legacyTypeQuestionIds,
        }),
    ),
  })
}

export function buildMiaolinChannelTypeWebhookForm({
  baseForm,
  channel,
  options,
}: {
  baseForm: PublicForm
  channel: string
  options: MiaolinDynamicOption[]
}): PublicForm {
  const channelQuestionId =
    getMiaolinChannelQuestionId(baseForm.questions) ?? MIAOLIN_CHANNEL_QUESTION_ID
  const dynamicTypeQuestionId =
    getMiaolinDynamicChannelTypeQuestionId(baseForm.questions) ??
    MIAOLIN_DYNAMIC_CHANNEL_TYPE_QUESTION_ID
  const legacyTypeQuestionIds = getMiaolinLegacyCustomerTypeQuestionIds(baseForm.questions)
  const dynamicChoices = createMiaolinChoices(dynamicTypeQuestionId, options)
  const questions = baseForm.questions
    .filter((question) => !isMiaolinLegacyCustomerTypeQuestion(question))
    .map((question) => {
      if (!isMiaolinDynamicChannelTypeQuestion(question)) return question
      const dependentChoices = dynamicChoices.map((choice) => choice.value)
      return {
        ...question,
        choices: dynamicChoices,
        settings: {
          ...question.settings,
          placeholder: '請選擇型態別',
          dependsOnQuestionId: channelQuestionId,
          sourceDependsOnQuestionId: MIAOLIN_CHANNEL_QUESTION_ID,
          dependentSource: 'miaolin_channel_types_webhook',
          dependentChoicesByQuestionValue: {
            [channel]: dependentChoices,
          },
          dependentChoiceEmptyMessage: '請先選擇客戶通路別',
          conditionalDisplay: {
            controllerQuestionId: channelQuestionId,
            controllerValue: channel,
            mode: 'show_when_equals',
            source: 'miaolin-channel-types-webhook',
            sourceControllerQuestionId: MIAOLIN_CHANNEL_QUESTION_ID,
          },
          dynamicOptions: {
            kind: 'miaolin_channel_types',
            providerMode: 'webhook',
            sourceLabel: 'Miaolin channel type webhook',
            controllerQuestionId: channelQuestionId,
            sourceControllerQuestionId: MIAOLIN_CHANNEL_QUESTION_ID,
            controllerValue: channel,
            providerState: 'loaded',
            fallback: 'dependent_question_choices',
          },
        },
      } satisfies Question
    })
  const logicJumps: LogicJump[] = [
    ...baseForm.logicJumps.filter(
      (jump) =>
        !isMiaolinChannelTypeLogicJump(jump, {
          channelQuestionId,
          dynamicTypeQuestionId,
          legacyTypeQuestionIds,
        }),
    ),
    {
      id: 'logic-miaolin-customer-channel-type-webhook',
      questionId: channelQuestionId,
      conditions: [{ field: channelQuestionId, operator: 'equals', value: channel }],
      operator: 'AND',
      targetQuestionId: dynamicTypeQuestionId,
      displayTargets: [
        {
          type: 'question',
          id: `question:${dynamicTypeQuestionId}`,
          questionId: dynamicTypeQuestionId,
        },
      ],
      order: 0,
    },
  ]

  return cloneFormWithRuntimeShape(baseForm, { questions, logicJumps })
}

function cloneFormWithRuntimeShape(
  baseForm: PublicForm,
  patch: Pick<PublicForm, 'questions' | 'logicJumps'>,
): PublicForm {
  const questionIds = new Set(patch.questions.map((question) => question.id))
  return {
    ...baseForm,
    questions: patch.questions,
    logicJumps: patch.logicJumps,
    questionGroups: baseForm.questionGroups?.map((group) => ({
      ...group,
      questionIds: group.questionIds.filter((questionId) => questionIds.has(questionId)),
      questions: group.questions?.filter((question) => questionIds.has(question.id)),
    })),
  }
}

function createMiaolinChoices(questionId: string, options: MiaolinDynamicOption[]): Choice[] {
  return options.map((option, index) => ({
    id: `${questionId}-webhook-choice-${index + 1}`,
    questionId,
    label: option.label,
    value: option.value,
    orderIndex: index,
  }))
}

function isMiaolinChannelTypeLogicJump(
  jump: LogicJump,
  {
    channelQuestionId = MIAOLIN_CHANNEL_QUESTION_ID,
    dynamicTypeQuestionId = MIAOLIN_DYNAMIC_CHANNEL_TYPE_QUESTION_ID,
    legacyTypeQuestionIds = new Set<string>(),
  }: {
    channelQuestionId?: string
    dynamicTypeQuestionId?: string
    legacyTypeQuestionIds?: Set<string>
  } = {},
) {
  const targetQuestionId = typeof jump.targetQuestionId === 'string' ? jump.targetQuestionId : ''
  const displayTargetQuestionIds = new Set(
    jump.displayTargets
      ?.map((target) => ('questionId' in target ? target.questionId : null))
      .filter((questionId): questionId is string => typeof questionId === 'string') ?? [],
  )
  return (
    jump.id.startsWith(MIAOLIN_CHANNEL_TYPE_LOGIC_PREFIX) ||
    (jump.questionId === channelQuestionId &&
      targetQuestionId.startsWith(`${MIAOLIN_DYNAMIC_CHANNEL_TYPE_QUESTION_ID}-type-`)) ||
    legacyTypeQuestionIds.has(targetQuestionId) ||
    hasSetOverlap(legacyTypeQuestionIds, displayTargetQuestionIds) ||
    (jump.questionId === channelQuestionId && targetQuestionId === dynamicTypeQuestionId)
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function hasSetOverlap(left: Set<string>, right: Set<string>) {
  for (const value of right) {
    if (left.has(value)) return true
  }
  return false
}

function isMiaolinChannelQuestion(question: Question) {
  return (
    question.id === MIAOLIN_CHANNEL_QUESTION_ID ||
    getQuestionSourceId(question) === MIAOLIN_CHANNEL_QUESTION_ID ||
    getQuestionFieldCode(question) === 'customer_channel' ||
    question.settings.customerFieldTarget === 'customerChannel' ||
    getQuestionDbFieldName(question) === 'customer_channel'
  )
}

function isMiaolinCustomerTypeQuestion(question: Question) {
  return (
    isMiaolinSingleCustomerTypeQuestion(question) || isMiaolinLegacyCustomerTypeQuestion(question)
  )
}

function isMiaolinSingleCustomerTypeQuestion(question: Question) {
  if (isMiaolinLegacyCustomerTypeQuestion(question)) return false
  const dynamicOptions = isRecord(question.settings.dynamicOptions)
    ? question.settings.dynamicOptions
    : null
  return (
    question.id === MIAOLIN_DYNAMIC_CHANNEL_TYPE_QUESTION_ID ||
    getQuestionSourceId(question) === MIAOLIN_DYNAMIC_CHANNEL_TYPE_QUESTION_ID ||
    getQuestionFieldCode(question) === 'customer_type' ||
    question.settings.customerFieldTarget === 'customerType' ||
    getQuestionDbFieldName(question) === 'customer_type' ||
    dynamicOptions?.kind === 'miaolin_channel_types'
  )
}

function isMiaolinLegacyCustomerTypeQuestion(question: Question) {
  const sourceQuestionId = getQuestionSourceId(question)
  return (
    question.settings.splitFromQuestionId === MIAOLIN_CHANNEL_TYPE_SPLIT_FROM_ID ||
    question.id.startsWith(`${MIAOLIN_DYNAMIC_CHANNEL_TYPE_QUESTION_ID}-type-`) ||
    sourceQuestionId?.startsWith(`${MIAOLIN_DYNAMIC_CHANNEL_TYPE_QUESTION_ID}-type-`) === true ||
    question.title.startsWith('型態別（')
  )
}

function getMiaolinLegacyCustomerTypeQuestionIds(questions: Question[]) {
  return new Set(
    questions
      .filter((question) => isMiaolinLegacyCustomerTypeQuestion(question))
      .map((question) => question.id),
  )
}

function getQuestionSourceId(question: Question) {
  const sourceQuestionId = question.settings.sourceQuestionId
  return typeof sourceQuestionId === 'string' ? sourceQuestionId : null
}

function getQuestionFieldCode(question: Question) {
  const fieldCode = (question as Question & { fieldCode?: unknown }).fieldCode
  if (typeof fieldCode === 'string') return fieldCode
  const settingsFieldCode = question.settings.fieldCode
  return typeof settingsFieldCode === 'string' ? settingsFieldCode : null
}

function getQuestionDbFieldName(question: Question) {
  const dbFieldName = question.settings.dbFieldName
  if (typeof dbFieldName === 'string') return dbFieldName
  const sharedDbFieldName = question.settings.sharedDbFieldName
  return typeof sharedDbFieldName === 'string' ? sharedDbFieldName : null
}
